import { captureDesktopSyncOwner, ownedSyncRead, captureCacheBinding, ownedCacheWrite, retiredSyncResult, sameSyncOwner } from './desktopSyncOwnership';
import axios from 'axios';

import {
  getActiveAuthToken,
  getActiveSessionRevision,
  getActiveSessionRoles,
  getActiveSessionType,
  SESSION_TYPE_CLOUD,
} from '@/api/authenticatedClient';
import { getPublicShopBranding } from '@/api/shop';
import { projectShopRouteOrigin } from '@/utils/shopRouteOrigin';
import { getDesktopUserDirectory } from '@/api/users';
import { buildOfflineUserReadModel } from '@/utils/offlineUsers';

// Tracks which logo (by id) we've already pushed bytes for, so the ~5 MB image
// isn't re-sent every sync — only when the shop actually changes its logo.
export const CACHED_LOGO_ID_KEY = 'lvb_cached_logo_id';

function desktopConfig() {
  return typeof window !== 'undefined' ? window.lvbDesktop : null;
}

export const RUN_CACHE_LAST_SYNC_KEY = 'lvb_run_cache_last_sync';
export const DEVICE_STATUS_EVENT = 'lvb:device-authorization-status';

export function getRunCacheLastUpdated() {
  return localStorage.getItem(RUN_CACHE_LAST_SYNC_KEY);
}

// Online means the app is pointed at the cloud, not the local backend it mirrors
// into.
function isOnline(desktop) {
  return Boolean(
    desktop &&
    desktop.localBackendUrl &&
    desktop.backendUrl !== desktop.localBackendUrl,
  );
}

// True when running inside the desktop (Electron) app at all, online or offline.
export function isDesktopApp() {
  return Boolean(desktopConfig());
}

// True when running in the desktop app and currently served by the local backend
// (i.e. the cloud is unreachable) — the signal to show the read-only offline view.
export function isDesktopOffline() {
  const desktop = desktopConfig();
  return Boolean(
    desktop &&
    desktop.localBackendUrl &&
    desktop.backendUrl === desktop.localBackendUrl,
  );
}

export async function refreshDesktopConnectivity() {
  const desktop = desktopConfig();
  if (!desktop || typeof desktop.refreshConnectivity !== 'function') {
    return {
      desktop: Boolean(desktop),
      offline: isDesktopOffline(),
      changed: false,
    };
  }
  const result = await desktop.refreshConnectivity();
  return {
    desktop: true,
    online: Boolean(result?.online),
    offline: result?.backendUrl === result?.localBackendUrl,
    changed: Boolean(result?.changed),
  };
}

export async function offlineAdminLogin(username, password) {
  const desktop = desktopConfig();
  if (
    !desktop
    || typeof desktop.offlineAdminLogin !== 'function'
    || !isDesktopOffline()
  ) {
    throw new Error('Offline sign-in is temporarily unavailable. Restart the desktop app and try again.');
  }
  const result = await desktop.offlineAdminLogin({ username, password });
  if (!result?.ok || !result.token || !result.user) {
    if (result?.reason === 'online_login_required') {
      throw new Error('Connect to the internet and sign in once to enable offline sign-in on this computer.');
    }
    if (result?.reason === 'local_services_unavailable') {
      throw new Error('Offline services are unavailable on this computer. An administrator can retry them in Settings → Devices.');
    }
    throw new Error(
      result?.reason === 'throttled'
        ? 'Too many offline sign-in attempts. Try again later.'
        : 'Offline sign-in failed. This admin must have signed in online on this desktop first.',
    );
  }
  return result;
}

// Electron main exclusively owns order downloads and cloud credentials. This
// renderer adapter only translates the manager's in-memory aggregate status
// into the per-resource result consumed by protected-store recovery.
export async function syncOrderSnapshot(statusOverride = null) {
  const desktop = desktopConfig();
  const getStatus = desktop?.getOrderDataStatus;
  if (!statusOverride && typeof getStatus !== 'function') {
    return { state: 'failed', reason: 'order_data_status_bridge_unavailable' };
  }
  try {
    const result = statusOverride || await getStatus();
    if (result?.receiptAccepted === true) {
      return {
        state: 'completed',
        receiptAccepted: true,
        receipt: result.receipt || null,
      };
    }
    return {
      state: 'pending',
      receiptAccepted: false,
      reason: result?.operationReason || result?.reason || 'accepted_baseline_pending',
    };
  } catch (error) {
    return { state: 'failed', reason: error?.message || 'order_data_status_unavailable' };
  }
}

function hasActiveCloudSession() {
  return (
    getActiveSessionType() === SESSION_TYPE_CLOUD
    && Boolean(getActiveAuthToken())
  );
}

const normalizedRoleSet = (context) => new Set(
  (Array.isArray(context?.roles) ? context.roles : [])
    .filter((role) => typeof role === 'string')
    .map((role) => role.trim().toLowerCase())
    .filter(Boolean),
);

function contextHasRole(context, allowedRoles) {
  const roles = normalizedRoleSet(context);
  return allowedRoles.some((role) => roles.has(role));
}

function roleSetsMatch(leftRoles, rightRoles) {
  const left = Array.from(normalizedRoleSet({ roles: leftRoles })).sort();
  const right = Array.from(normalizedRoleSet({ roles: rightRoles })).sort();
  return left.length === right.length && left.every((role, index) => role === right[index]);
}

function capturedCloudSessionIsCurrent(context) {
  return (
    (!context?.owner || context.owner.current())
    && Number.isInteger(context?.sessionRevision)
    && context.sessionRevision === getActiveSessionRevision()
    && roleSetsMatch(context?.roles, getActiveSessionRoles())
    && hasActiveCloudSession()
  );
}

function replayResultFromCloudResponse(response) {
  const data = response?.data || {};
  if (data.state === 'applied' || data.applied === true) {
    return {
      state: 'applied',
      detail: data.reason || 'applied',
      cloud_order: data.cloud_order,
    };
  }
  if (data.state === 'conflict') {
    return {
      state: 'conflict',
      detail: data.reason || 'conflict',
      cloud_order: data.cloud_order || data.cloud_snapshot,
    };
  }
  return {
    state: 'failed_retryable',
    detail: 'Unexpected replay response',
  };
}

function replayResultFromError(error) {
  const status = error?.response?.status;
  if ([401, 403].includes(status)) {
    return { state: 'failed_retryable', detail: `cloud_replay_authority_${status}` };
  }
  if ([408, 409, 425, 429].includes(status)) {
    return { state: 'failed_retryable', detail: `cloud_replay_retryable_${status}` };
  }
  if (status && status < 500) {
    return { state: 'failed_terminal', detail: `cloud_replay_contract_${status}` };
  }
  return {
    state: 'failed_retryable',
    detail: error?.message || 'Replay failed',
  };
}

/**
 * Claim one queue of offline order changes, send each to the cloud, and record
 * what came back.
 *
 * Status changes and field edits do exactly this, and differed only in the two
 * path fragments below. `queue` names the local queue; `cloudEndpoint` names the
 * cloud route that applies that kind of change.
 *
 * See TCA-26081202.
 */
async function replayCachedOrderMutations({ queue, cloudEndpoint, context }) {
  const desktop = desktopConfig();
  const idle = { claimed: 0, reviewMayHaveChanged: false, acknowledgementFailures: 0 };
  if (!isOnline(desktop)) return idle;
  if (!contextHasRole(context, ['admin', 'designer'])) return idle;
  if (!capturedCloudSessionIsCurrent(context)) return idle;
  let acknowledgementFailures = 0;
  try {
    const claim = await axios.post(
      `${desktop.localBackendUrl}/api/desktop/replay/${queue}/claim`,
    );
    // TCA-26100503: retain the claim's cache owner through the entire cloud
    // round trip. Never fetch a newer tag to make a stale acknowledgement fit.
    // Absence remains compatible with the existing schema-1 desktop backend;
    // the new-schema replacement path is still disabled during integration.
    const cacheBinding = claim.headers?.get?.('X-LVB-Cache-Binding')
      ?? claim.headers?.['x-lvb-cache-binding'];
    if (cacheBinding !== undefined && cacheBinding !== null
        && (typeof cacheBinding !== 'string' || !/^[a-f0-9]{64}$/.test(cacheBinding))) {
      throw new Error('invalid_cache_operation_binding');
    }
    const acknowledgementOptions = cacheBinding
      ? [{ headers: { 'X-LVB-Cache-Binding': cacheBinding } }] : [];
    const mutations = claim.data || [];
    await Promise.all((mutations || []).map(async (mutation) => {
      if (!capturedCloudSessionIsCurrent(context)) return;
      let result;
      try {
        if (mutation.edit_protocol === 2) {
          const capabilities = await axios.get(`${desktop.backendUrl}/api/offline-edit-capabilities`).catch(() => null);
          if (capabilities?.data?.edit_protocol !== 2 || capabilities.data.ledger_generation !== 1 || !capturedCloudSessionIsCurrent(context)) {
            throw new Error('Cloud edit protocol unavailable; update cloud before replay');
          }
        }
        const cloud = await axios.post(
          `${desktop.backendUrl}/api/orders/${mutation.target_id}/${cloudEndpoint}`,
          mutation,
        );
        result = replayResultFromCloudResponse(cloud);
      } catch (error) {
        result = replayResultFromError(error);
      }
      // A cloud 401 clears the session in the shared response interceptor. A
      // login, logout, or role replacement can do the same while the request is
      // in flight. Never acknowledge the old lease under the new session; lease
      // expiry and compare-and-swap replay recover it safely.
      if (!capturedCloudSessionIsCurrent(context)) return;
      try {
        await axios.post(
          `${desktop.localBackendUrl}/api/desktop/replay/${queue}/${mutation.id}/result`,
          { ...result, lease_id: mutation.lease_id },
          ...acknowledgementOptions,
        );
      } catch (error) {
        // Without a reset, the record stays claimed until its lease expires,
        // then a later tick
        // picks it up again. Cloud replay is compare-and-swap, so that retry
        // reports `already_applied` rather than applying anything twice.
        // After a reset, an old binding is refused; never retry it against the
        // fresh work epoch or imply that the cloud action was undone.
        // Surfaced rather than swallowed: a persistent failure here used to be
        // invisible, and every record it touched was stranded permanently.
        acknowledgementFailures += 1;
        console.warn(
          `[replay] could not record ${queue} result for ${mutation.id}:`,
          error?.message || error,
        );
      }
    }));
    return {
      claimed: mutations.length,
      reviewMayHaveChanged: mutations.length > 0,
      acknowledgementFailures,
    };
  } catch {
    // Transient error, offline, or session gone — leave the queue untouched.
    return { claimed: 0, reviewMayHaveChanged: false, acknowledgementFailures };
  }
}

export async function replayCachedOrderStatusMutations(context) {
  return replayCachedOrderMutations({
    queue: 'cached-order-status',
    cloudEndpoint: 'replay-status-mutation',
    context,
  });
}

export async function replayCachedOrderFieldMutations(context) {
  return replayCachedOrderMutations({
    queue: 'cached-order-field',
    cloudEndpoint: 'replay-field-mutation',
    context,
  });
}

// Fetch the cloud logo as base64 so the local backend can serve it offline.
// Uses arraybuffer + btoa (no FileReader) so it's deterministic and testable.
async function fetchLogoBase64(owner) {
  try {
    const res = await ownedSyncRead(owner, config => axios.get(`${owner.cloud}/api/public/shop-logo`, {
      ...config, responseType: 'arraybuffer',
    }));
    const bytes = new Uint8Array(res.data);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
    return { base64: btoa(binary), contentType: res.headers?.['content-type'] || 'image/png' };
  } catch { return null; }
}

// Persisted logo id is only a hint. Trust bytes acknowledged for this context/cache binding.
const acknowledgedLogos = new WeakMap();
export async function syncShopIdentity(context) {
  const owner = captureDesktopSyncOwner(context);
  if (!owner.current()) return retiredSyncResult();
  try {
    const binding = await captureCacheBinding(owner);
    const branding = await ownedSyncRead(owner, config => getPublicShopBranding(config, owner.cloud));
    if (!branding || typeof branding !== 'object' || Array.isArray(branding)) throw new Error('invalid_shop');
    const payload = { ...branding, route_origin: projectShopRouteOrigin(branding.route_origin) };
    const logoId = branding.logo_id || null;
    const known = acknowledgedLogos.get(owner.bridge);
    const cachedLogoId = known && sameSyncOwner(known.owner, owner) && known.binding === binding ? known.logoId : null;
    let acknowledgedLogo = cachedLogoId;
    if (!logoId) acknowledgedLogo = null;
    else if (logoId !== cachedLogoId) {
      const logo = await fetchLogoBase64(owner);
      if (logo) {
        payload.logo_base64 = logo.base64;
        payload.logo_content_type = logo.contentType;
        acknowledgedLogo = logoId;
      }
    }
    if (!owner.current()) return retiredSyncResult();
    return await ownedCacheWrite(owner, 'shop', payload, binding, data => data?.cached === true, () => {
      acknowledgedLogos.set(owner.bridge, { owner, binding, logoId: acknowledgedLogo });
      if (acknowledgedLogo) localStorage.setItem(CACHED_LOGO_ID_KEY, acknowledgedLogo);
      else if (!logoId) localStorage.removeItem(CACHED_LOGO_ID_KEY);
    });
  } catch (error) {
    return owner.current() ? { state: 'failed', reason: error?.message || 'unknown' } : retiredSyncResult();
  }
}

// Product-catalog synchronization is owned by Electron's main process. The
// renderer intentionally has no package transfer or local apply method.

// Cache delivery runs + the user read-model into the local DB so the offline
// Deliver tab can show each run, its ordered stops (joined against cached orders
// when a run is opened), plus readable user names/roles/colors for drivers,
// designers, attribution, and review cards. Dedicated synchronization sources
// return complete bounded cohorts (up to 1,000 runs / 100 staff) or refuse the
// refresh; failures preserve the previous mirror, never apply a truncated UI
// list. Users retain only the safe display/workflow projection. Read-only:
// user management stays online.
// Runs only in the desktop app, online, and logged in.
export async function syncRuns(context) {
  const desktop = desktopConfig();
  const owner = captureDesktopSyncOwner(context);
  if (!isOnline(desktop)) return { state: 'skipped', reason: 'offline' };
  if (!contextHasRole(context, ['admin'])) {
    return { state: 'skipped', reason: 'routes_read_required' };
  }
  if (!capturedCloudSessionIsCurrent(context)) {
    return { state: 'skipped', reason: 'session_changed' };
  }
  try {
    if (typeof desktop?.synchronizeRunCache !== 'function') {
      return { state: 'failed', reason: 'native_run_sync_unavailable' };
    }
    const result = await desktop.synchronizeRunCache();
    if (!capturedCloudSessionIsCurrent(context) || !owner.current()) {
      return { state: 'skipped', reason: 'session_changed' };
    }
    if (result?.ok !== true || result.state !== 'current') {
      return { state: result?.state === 'skipped' ? 'skipped' : 'failed',
        reason: result?.reason || 'native_run_sync_failed' };
    }
    localStorage.setItem(RUN_CACHE_LAST_SYNC_KEY, new Date().toISOString());
    return { state: 'cached', count: result.runs };
  } catch (error) {
    console.warn('[desktop-cache] run synchronization failed:', error?.message || error);
    return { state: 'failed', reason: error?.message || 'unknown' };
    // Transient error, offline, or session gone — leave the cache untouched.
  }
}

// Cache the safe staff read model independently. A failed run fetch must not
// prevent offline sign-in display or driver assignment from receiving users.
export async function syncUserDirectory(context) {
  const owner = captureDesktopSyncOwner(context);
  if (!owner.current()) return retiredSyncResult();
  try {
    const binding = await captureCacheBinding(owner);
    const source = await ownedSyncRead(owner, config => getDesktopUserDirectory(config, owner.cloud));
    if (!Array.isArray(source)) throw new Error('invalid_user_directory');
    const users = buildOfflineUserReadModel(source);
    if (users.length !== source.length) throw new Error('invalid_user_directory');
    return await ownedCacheWrite(owner, 'users', users, binding, data => data?.users === users.length);
  } catch (error) {
    return owner.current() ? { state: 'failed', reason: error?.message || 'unknown' } : retiredSyncResult();
  }
}

// Ask Electron's main process to enroll/heartbeat the installation. Electron
// obtains the active cloud token from its own in-memory session; the renderer
// supplies no credential. Rides the existing one-minute desktop sync batch.
export async function syncHeartbeat() {
  const desktop = desktopConfig();
  if (!isOnline(desktop)) return { state: 'skipped', reason: 'offline' };
  if (!hasActiveCloudSession()) return { state: 'skipped', reason: 'logged_out' };
  if (typeof desktop.syncDeviceIdentity !== 'function') {
    return { state: 'skipped', reason: 'bridge_unavailable' };
  }
  try {
    const status = await desktop.syncDeviceIdentity();
    if (status && typeof window !== 'undefined' && window.lvbDesktop === desktop) {
      window.dispatchEvent(new CustomEvent(DEVICE_STATUS_EVENT, {
        detail: status,
      }));
    }
    return { state: 'completed', status };
  } catch (error) {
    return { state: 'failed', reason: error?.message || 'unknown' };
    // Transient — the next tick retries.
  }
}
